/*
 * customers.ts — Operational order search, multi-selection, rendering, and file upload.
 *
 * Multiple orders can be selected via checkbox, including across different searches
 * (selecting an order does not require it to still be visible in the current search
 * results). Uploading sends every selected file to every selected order.
 */

/* global document, window */

import type { BcOperationalOrder } from "./bc-api";
import { linkOrderDocument } from "./bc-api";
import { getOrCreateAttachmentDetails } from "./attachment-details";
import {
  searchOperationalOrders as searchOrdersApi,
  findOperationalOrderByNo,
  findOperationalOrderByInternalReference,
  findOrdersUrlForOrderNos,
  isFileAlreadyLinked,
} from "./bc-service";
import { ensureAccessToken, getBcRequestContext, loadSettings } from "./taskpane-auth";
import { isDepartmentLoadPending } from "./companies";
import { getCurrentAccount, forceRefreshAccessToken, getTokenExpiresAtMs } from "../shared/auth-state";
import type { BcRequestContext } from "../shared/auth-state";
import { BcHttpError } from "../shared/odata";
import {
  collectedEmails,
  localFiles,
  getEmailFileId,
  getAttachmentFileId,
  withMailItem,
  isFileNameUploaded,
  isFileChecked,
} from "./email-context";
import {
  escapeHtml,
  formatSize,
  normalizeFileNameForCompare,
  normalizeEmlFileName,
  base64ToUint8Array,
  getErrorMessage,
  withRetry,
} from "./helpers";
import type { CollectedEmail } from "./email-context";
import type { LoadedMessageExt, AttachmentDetailsExt } from "../shared/office-compat";

// {4 capital letters/digits}{3 capital letters}{2 non-zero digits}-{digits}{3 capital letters}
// e.g. AB12ABC12-34567ABC. Fallback pattern tried when no order-number candidate matches.
const INTERNAL_REFERENCE_RE = /^[A-Z0-9]{4}[A-Z]{3}[1-9]{2}-\d{6}[A-Z]{3}$/;


// ─── Types ──────────────────────────────────

interface UploadCandidate {
  id: string;
  fileName: string;
  doctype?: string;
  comments?: string;
  allowSynchToPortal?: boolean;
  readBytes: () => Promise<Uint8Array>;
}

type PrimaryActionMode = "none" | "upload";

// ─── State ──────────────────────────────────

const MAX_UPLOAD_BYTES = 100 * 1024 * 1024; // 100 MB practical limit for BC OData uploads

let allOrders: BcOperationalOrder[] = [];
// Selected orders, keyed by order key. Persists across searches -- an order selected
// while searching "SO-100" stays selected after searching "SO-200".
let selectedBcRecords = new Map<string, BcOperationalOrder>();
// Keys within selectedBcRecords that were selected by autoSelectOrdersFromEmailSubjects
// rather than a manual checkbox click. Unlike manual selections, these are scoped to
// "whatever the currently selected email(s) match" -- re-evaluated (and stale entries
// dropped) on every auto-select pass, instead of persisting forever. A manual click on an
// auto-selected order's checkbox removes it from this set, promoting it to a normal
// (persistent) selection.
let autoSelectedOrderKeys = new Set<string>();
let mapUploadInProgress = false;
let orderSearchDebounceHandle: number | null = null;
let orderSearchAbortController: AbortController | null = null;

// ─── Callbacks ──────────────────────────────

type RenderCallback = () => void;
let onAttachmentsChanged: RenderCallback | null = null;

export function setOnAttachmentsChanged(callback: RenderCallback): void {
  onAttachmentsChanged = callback;
}

// ─── State accessors ────────────────────────

/**
 * The orderNo of the single currently auto-selected order, or null if there isn't exactly
 * one (none, or several from multiple selected emails each matching a different order).
 * Used to scope the live "already uploaded" check on the attachments list -- see
 * refreshUploadedBadges in attachments.ts -- to a specific order when that's unambiguous,
 * falling back to an any-order check otherwise.
 */
export function getSingleAutoSelectedOrderNo(): string | null {
  if (autoSelectedOrderKeys.size !== 1) return null;
  const key = Array.from(autoSelectedOrderKeys)[0];
  return selectedBcRecords.get(key)?.orderNo ?? null;
}

// ─── Order key ──────────────────────────────

function getOrderKey(order: BcOperationalOrder | null): string {
  return String(order?.id ?? "");
}

// ─── Init ───────────────────────────────────

export function initBcSearch(): void {
  const searchInput = document.getElementById("bc-search") as HTMLInputElement;
  searchInput?.addEventListener("input", () => {
    queueSearchOrders(searchInput.value.trim());
  });
  document.getElementById("btn-refresh-customers")?.addEventListener("click", () => {
    resetOrderSearchAndSelection();
  });

  loadOrders();
}

/** Cancels any in-flight/pending order search and empties the search box, without touching
 *  selection state. The only caller that needs the search box cleared without also wiping
 *  the selection is resetOrderSearchAndSelection itself. */
function clearOrderSearchInput(): void {
  orderSearchAbortController?.abort();
  if (orderSearchDebounceHandle !== null) {
    window.clearTimeout(orderSearchDebounceHandle);
    orderSearchDebounceHandle = null;
  }

  const searchInput = document.getElementById("bc-search") as HTMLInputElement | null;
  if (searchInput) searchInput.value = "";
}

/**
 * Clears the search box, the current search results, and the entire order selection --
 * manual and auto alike. Used both by the refresh button and (see onItemChanged in
 * taskpane.ts) on every email switch: a selection made for one email carrying over to a
 * different one is a real mis-upload risk (attaching the new email's files to an order that
 * has nothing to do with it), so nothing here is allowed to persist across a switch the way
 * it deliberately used to. autoSelectOrdersFromEmailSubjects (called right after, once the
 * new email's subject is known) is what repopulates the selection -- a match is auto-picked,
 * no match leaves it blank rather than falling back to anything stale.
 */
export function resetOrderSearchAndSelection(): void {
  clearOrderSearchInput();

  allOrders = [];
  selectedBcRecords = new Map();
  autoSelectedOrderKeys = new Set();

  if (onAttachmentsChanged) onAttachmentsChanged();
  void loadOrders(true);
}

export function initMapUploadAction(): void {
  document.getElementById("btn-map-upload")?.addEventListener("click", async () => {
    await executePrimaryAction();
  });
  document.getElementById("map-upload-status-close")?.addEventListener("click", () => {
    clearMapUploadStatus();
  });
  document.getElementById("order-search-status-close")?.addEventListener("click", () => {
    clearOrderSearchStatus();
  });
  updateMapUploadButtonState();
}

// ─── Load / initial state ────────────────────

/**
 * Every order lookup here (manual search and both autosearch paths) is department-scoped
 * and fails closed to zero results when departmentSetEntryIdFlt hasn't loaded -- see
 * withDepartmentFilter in bc-api.ts. Surfaced via the dismissible order-search-status banner
 * (see setOrderSearchStatus) rather than folded into the list's own "no orders found"/"type
 * a number" hints, since no search here could ever succeed until that value loads. Called
 * from every path that knows the current department value (loadOrders and searchOrders) so
 * dismissing the banner is never "final" -- the next search attempt re-evaluates and shows
 * it again if the value is still missing.
 */
function updateDepartmentStatusBanner(departmentSetEntryIdFlt: string): void {
  // Blank in storage doesn't by itself mean "confirmed no department" -- it's cleared up
  // front the moment a company is selected, before the fetch that would actually answer that
  // question resolves (see the change handler in companies.ts). Showing the banner during
  // that gap would flash it on every company switch, correct or not, purely because a stray
  // loadOrders()/searchOrders() call happened to land mid-fetch. Only render either state
  // once isDepartmentLoadPending says we actually know.
  if (isDepartmentLoadPending()) {
    clearOrderSearchStatus();
    return;
  }
  if (!departmentSetEntryIdFlt.trim()) {
    setOrderSearchStatus("There is no Department User set up for you in this company.", "error");
  } else {
    clearOrderSearchStatus();
  }
}

export async function loadOrders(_forceReevaluate = false): Promise<void> {
  const token = await ensureAccessToken(false);
  if (!token) {
    setBcRecordsHtml('<div class="bc-error">&#128274; Not signed in. Open Settings to authenticate.</div>');
    selectedBcRecords = new Map();
    autoSelectedOrderKeys = new Set();
    clearOrderSearchStatus();
    updateMapUploadButtonState();
    return;
  }

  const { bcUrl, companyId, departmentSetEntryIdFlt } = loadSettings();
  if (!bcUrl) {
    setBcRecordsHtml('<div class="bc-error">&#9888;&#65039; No BC API endpoint configured. Open Settings.</div>');
    selectedBcRecords = new Map();
    autoSelectedOrderKeys = new Set();
    clearOrderSearchStatus();
    updateMapUploadButtonState();
    return;
  }
  if (!companyId) {
    setBcRecordsHtml('<div class="bc-hint">Select a company to search operational orders.</div>');
    selectedBcRecords = new Map();
    autoSelectedOrderKeys = new Set();
    clearOrderSearchStatus();
    updateMapUploadButtonState();
    return;
  }

  updateDepartmentStatusBanner(departmentSetEntryIdFlt);

  // A non-empty search box means there's a live query the results should still reflect --
  // re-run it instead of falling through to the hint/selected-only reset below, which
  // otherwise wiped the results (back to a hint, or to just the current selection) on every
  // selection refresh while leaving whatever the user had typed sitting in the box,
  // unrelated to what was now showing underneath it.
  const searchInput = document.getElementById("bc-search") as HTMLInputElement | null;
  const query = searchInput?.value.trim() ?? "";
  if (query) {
    await searchOrders(query);
    return;
  }

  // Preserve whatever's currently selected (manual and/or auto) instead of unconditionally
  // wiping to a hint -- otherwise every selection refresh would flash the current selection
  // away right before autoSelectOrdersFromEmailSubjects() puts it (or its replacement) back.
  if (selectedBcRecords.size > 0) {
    allOrders = Array.from(selectedBcRecords.values());
    renderBcRecords(allOrders);
  } else {
    allOrders = [];
    setBcRecordsHtml('<div class="bc-hint">Type an order number to search.</div>');
  }
  updateMapUploadButtonState();
}

// ─── Search ─────────────────────────────────

function queueSearchOrders(query: string): void {
  if (orderSearchDebounceHandle !== null) {
    window.clearTimeout(orderSearchDebounceHandle);
  }
  orderSearchAbortController?.abort();
  orderSearchDebounceHandle = window.setTimeout(() => {
    void searchOrders(query);
  }, 220);
}

async function searchOrders(query: string): Promise<void> {
  const normalized = query.trim();
  const { companyId, departmentSetEntryIdFlt } = loadSettings();

  if (companyId) {
    updateDepartmentStatusBanner(departmentSetEntryIdFlt);
  } else {
    clearOrderSearchStatus();
  }

  if (!normalized) {
    if (selectedBcRecords.size > 0) {
      allOrders = Array.from(selectedBcRecords.values());
      renderBcRecords(allOrders);
    } else {
      allOrders = [];
      setBcRecordsHtml('<div class="bc-hint">Type an order number to search.</div>');
    }
    updateMapUploadButtonState();
    return;
  }

  if (!companyId) {
    allOrders = [];
    selectedBcRecords = new Map();
    autoSelectedOrderKeys = new Set();
    setBcRecordsHtml('<div class="bc-hint">Select a company to search operational orders.</div>');
    updateMapUploadButtonState();
    return;
  }
  if (!departmentSetEntryIdFlt.trim()) {
    allOrders = [];
    setBcRecordsHtml('<div class="bc-hint">No orders found for this number.</div>');
    updateMapUploadButtonState();
    return;
  }

  orderSearchAbortController?.abort();
  orderSearchAbortController = new AbortController();
  const signal = orderSearchAbortController.signal;

  try {
    const bc = await getBcRequestContext(false);
    setBcRecordsHtml('<div class="bc-loading">&#9203; Searching orders...</div>');

    const found = await searchOrdersApi(normalized, bc, { signal });

    // Selections are intentionally left untouched here -- they persist even when the
    // order they refer to isn't part of the current search results (see file header).
    // Any selected order missing from this search's results is pinned at the top, so a
    // multi-order selection stays visible while searching for other orders to add.
    const foundKeys = new Set(found.map((o) => getOrderKey(o)));
    const pinnedSelected = Array.from(selectedBcRecords.values()).filter((o) => !foundKeys.has(getOrderKey(o)));
    allOrders = [...pinnedSelected, ...found];
    if (allOrders.length === 0) {
      setBcRecordsHtml('<div class="bc-hint">No orders found for this number.</div>');
    } else {
      renderBcRecords(allOrders);
    }
    updateMapUploadButtonState();
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === "AbortError") return;
    const message = getErrorMessage(err);
    allOrders = [];
    if (message.toLowerCase().includes("company is not selected")) {
      setBcRecordsHtml('<div class="bc-hint">Select a company to search operational orders.</div>');
    } else {
      setBcRecordsHtml(`<div class="bc-error">&#10060; ${escapeHtml(message)}</div>`);
    }
    updateMapUploadButtonState();
  }
}

// ─── Auto-select from email subject ─────────

/**
 * Splits a subject into words and trims surrounding punctuation off each, deduplicated in
 * the order they appear. Both whitespace and underscores are treated as separators, so a
 * subject like "BLA: Bla_AB12ABC12_note" still yields "AB12ABC12" as its own candidate
 * word. A dash is deliberately NOT a separator -- it appears inside internalReference-
 * shaped words (see INTERNAL_REFERENCE_RE, e.g. AB12ABC12-34567ABC); only leading/trailing
 * punctuation is stripped, internal dashes are left untouched.
 */
function splitSubjectWords(subject: string): string[] {
  const seen = new Set<string>();
  const words: string[] = [];
  for (const raw of subject.split(/[\s_]+/)) {
    const word = raw.replace(/^[^\w]+|[^\w]+$/g, "");
    if (!word || seen.has(word)) continue;
    seen.add(word);
    words.push(word);
  }
  return words;
}

/** Words starting with companyAcronym (case-sensitive) -- candidate order numbers. */
function extractOrderNoCandidates(subject: string, acronym: string): string[] {
  if (!acronym) return [];
  return splitSubjectWords(subject).filter((w) => w.startsWith(acronym));
}

/** Words shaped like an internalReference (case-sensitive) -- candidate fallback matches. */
function extractInternalReferenceCandidates(subject: string): string[] {
  return splitSubjectWords(subject).filter((w) => INTERNAL_REFERENCE_RE.test(w));
}

/**
 * Tries each candidate in turn -- an already-selected order matching it wins immediately
 * (no API call); otherwise it's looked up via `lookup`. Returns the first candidate's
 * match once one is found (a lookup returning exactly one order), or null if none of the
 * candidates resolves to exactly one order. Purely a lookup: does not touch selection
 * state, so a batch of these can run before any UI update happens (see
 * autoSelectOrdersFromEmailSubjects).
 */
async function findSingleMatch(
  candidates: string[],
  bc: BcRequestContext,
  lookup: (candidate: string, bc: BcRequestContext) => Promise<BcOperationalOrder[]>,
  isMatch: (order: BcOperationalOrder, candidate: string) => boolean,
): Promise<BcOperationalOrder | null> {
  for (const candidate of candidates) {
    const known = Array.from(selectedBcRecords.values()).find((o) => isMatch(o, candidate));
    if (known) return known;

    let results: BcOperationalOrder[];
    try {
      results = await lookup(candidate, bc);
    } catch (err: unknown) {
      console.error(`Auto order lookup failed for "${candidate}":`, getErrorMessage(err));
      continue;
    }

    if (results.length === 1) return results[0];
  }
  return null;
}

/** Order-number candidates first; internalReference candidates as the fallback. */
async function findMatchForSubject(
  subject: string,
  acronym: string,
  bc: BcRequestContext,
): Promise<BcOperationalOrder | null> {
  const orderNoMatch = await findSingleMatch(
    extractOrderNoCandidates(subject, acronym),
    bc,
    findOperationalOrderByNo,
    (order, candidate) => order.orderNo === candidate,
  );
  if (orderNoMatch) return orderNoMatch;

  return findSingleMatch(
    extractInternalReferenceCandidates(subject),
    bc,
    findOperationalOrderByInternalReference,
    (order, candidate) => order.internalReference === candidate,
  );
}

/**
 * For each currently selected email, resolves its auto-match (see findMatchForSubject),
 * then applies the whole new set as a single atomic diff against autoSelectedOrderKeys:
 * matches no longer present are deselected and dropped from allOrders entirely (not left
 * behind unchecked -- an order auto-selected for one email is not relevant once you've
 * moved to a different one), and newly matched orders are selected. Manually checked
 * orders (never added to autoSelectedOrderKeys, including ones manually unchecked -- see
 * toggleOrderSelection) are untouched either way.
 *
 * All lookups happen before any selection-state mutation, and there's exactly one render
 * at the end -- this matters because loadOrders() (called just before this on every
 * selection refresh) already shows whatever's currently selected; if this function mutated
 * state as it went, that display would flicker (clear, then repopulate after each network
 * round-trip) instead of just updating once when the new set is known.
 */
export async function autoSelectOrdersFromEmailSubjects(): Promise<void> {
  const { bcUrl, companyId, companyAcronym } = loadSettings();
  const acronym = companyAcronym.trim();

  const newMatches = new Map<string, BcOperationalOrder>();
  if (bcUrl && companyId && acronym && collectedEmails.length > 0) {
    let bc: BcRequestContext | null = null;
    try {
      bc = await getBcRequestContext(false);
    } catch {
      bc = null; // not signed in / no token -- silently skip, this is a background convenience
    }

    if (bc) {
      for (const email of collectedEmails) {
        const match = await findMatchForSubject(email.subject, acronym, bc);
        if (match) newMatches.set(getOrderKey(match), match);
      }
    }
  }

  let changed = false;

  for (const key of Array.from(autoSelectedOrderKeys)) {
    if (newMatches.has(key)) continue;
    selectedBcRecords.delete(key);
    autoSelectedOrderKeys.delete(key);
    allOrders = allOrders.filter((o) => getOrderKey(o) !== key);
    changed = true;
  }

  for (const order of newMatches.values()) {
    if (selectOrderProgrammatically(order, { auto: true })) changed = true;
  }

  if (changed) {
    renderBcRecords(allOrders);
    if (onAttachmentsChanged) onAttachmentsChanged();
    updateMapUploadButtonState();
  }
}

// ─── Render order list ───────────────────────

function renderBcRecords(orders: BcOperationalOrder[]): void {
  if (orders.length === 0) {
    setBcRecordsHtml('<div class="bc-hint">No orders found.</div>');
    return;
  }

  const html = orders
    .map((order) => {
      const key = getOrderKey(order);
      const isSelected = selectedBcRecords.has(key);
      const fields: Array<{ label: string; value: string }> = [
        { label: "Customer Name", value: order.customerName ?? "" },
        { label: "Internal Reference", value: order.internalReference ?? "" },
        { label: "External Reference", value: order.externalReference ?? "" },
        { label: "Booking Reference", value: order.bookingReference ?? "" },
        { label: "MBL No.", value: order.mblNo ?? "" },
      ];

      const rows = fields
        .filter((f) => f.value)
        .map((f) => `<div class="bc-record-field"><span class="bc-record-field-label">${escapeHtml(f.label)}</span><span class="bc-record-field-value">${escapeHtml(f.value)}</span></div>`)
        .join("");

      return `
      <div class="bc-record${isSelected ? " bc-record--selected" : ""}" data-id="${escapeHtml(key)}">
        <div class="bc-record-field bc-record-field--header">
          <input type="checkbox" class="bc-record-checkbox" data-id="${escapeHtml(key)}" ${isSelected ? "checked" : ""} />
          <span class="bc-record-field-value">${escapeHtml(order.orderNo ?? "")}</span>
        </div>
        ${rows}
      </div>`;
    })
    .join("");

  setBcRecordsHtml(html);

  document.querySelectorAll<HTMLInputElement>(".bc-record-checkbox").forEach((cb) => {
    cb.addEventListener("change", () => {
      const id = cb.dataset.id!;
      const item = orders.find((o) => getOrderKey(o) === id);
      if (item) toggleOrderSelection(item, cb.checked);
    });
  });

  document.querySelectorAll<HTMLElement>(".bc-record").forEach((row) => {
    row.addEventListener("click", (e) => {
      // A click landing on the checkbox itself already toggles it (and fires "change"
      // above) -- handling it again here would just flip it right back.
      if ((e.target as HTMLElement).closest(".bc-record-checkbox")) return;

      // Don't hijack a click-drag used to select field text for copying (e.g. the
      // customer name) -- only a plain click (nothing selected) toggles the row.
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) return;

      // .click() (rather than flipping .checked directly) fires the checkbox's own
      // "change" listener too, so selection state stays in one place.
      row.querySelector<HTMLInputElement>(".bc-record-checkbox")?.click();
    });
  });
}

function toggleOrderSelection(item: BcOperationalOrder, selected: boolean): void {
  const key = getOrderKey(item);
  // Deliberately does not touch autoSelectedOrderKeys: checking a brand-new order (from a
  // manual search) never added it there in the first place, so it's already a persistent
  // manual selection. Unchecking an order that *is* tracked there leaves it tracked -- the
  // next auto-select pass (on the next email switch) will sweep it away if it no longer
  // matches, rather than leaving a stale unchecked row behind.
  if (selected) {
    selectedBcRecords.set(key, item);
  } else {
    selectedBcRecords.delete(key);
  }

  const row = Array.from(document.querySelectorAll<HTMLElement>(".bc-record")).find((el) => el.dataset.id === key);
  row?.classList.toggle("bc-record--selected", selected);

  if (onAttachmentsChanged) onAttachmentsChanged();
  updateMapUploadButtonState();
}

/**
 * Marks an order selected without requiring it to be part of the currently rendered
 * search results (mirrors the "pinned selected" handling in searchOrders). Returns false
 * if the order was already selected, so callers can skip a redundant re-render. Pass
 * `auto: true` when called from auto-select so the key is tracked in autoSelectedOrderKeys
 * (and can be swept away on a later pass) -- only takes effect for a genuinely new
 * selection, never downgrading an existing manual selection to "auto".
 */
function selectOrderProgrammatically(order: BcOperationalOrder, opts?: { auto?: boolean }): boolean {
  const key = getOrderKey(order);
  const wasSelected = selectedBcRecords.has(key);
  if (!wasSelected) {
    selectedBcRecords.set(key, order);
    if (opts?.auto) autoSelectedOrderKeys.add(key);
  }
  if (!allOrders.some((o) => getOrderKey(o) === key)) {
    allOrders = [order, ...allOrders];
  }
  return !wasSelected;
}

function setBcRecordsHtml(html: string): void {
  const el = document.getElementById("bc-records-list");
  if (el) el.innerHTML = html;
}

// ─── Status UI ──────────────────────────────
//
// One small dismissible banner "mechanism" (.status-banner in the CSS), reused by two
// independent instances: #map-upload-status (Upload result, overlaying the Upload button so
// its own size never moves) and #order-search-status (department-missing error, a normal-flow
// banner above the action row -- see updateDepartmentStatusBanner). Each gets its own thin
// setXStatus/clearXStatus pair below so the two never clobber each other's message.

// Exported (unlike the rest of this section) so attachments.ts can drive its own
// "attachments may be incomplete" banner through the same mechanism -- see
// updateAttachmentsDegradedBanner there -- without duplicating this markup/CSS contract.
export type StatusBannerKind = "pending" | "success" | "error";
const STATUS_BANNER_KIND_CLASSES = ["status-banner--pending", "status-banner--success", "status-banner--error"];

export function setStatusBanner(elId: string, textElId: string, message: string, kind: StatusBannerKind = "pending"): void {
  const el = document.getElementById(elId);
  const textEl = document.getElementById(textElId);
  if (!el || !textEl) return;

  textEl.textContent = message;
  el.classList.remove(...STATUS_BANNER_KIND_CLASSES, "status-banner--visible");
  if (!message) return;
  el.classList.add(`status-banner--${kind}`, "status-banner--visible");
}

/**
 * Same as setStatusBanner, but a `linkText` substring of `message` (if present) is rendered
 * as a clickable hyperlink to `linkUrl` instead of plain text -- used to make the "Uploaded
 * n file(s) to m order(s)" segment of the post-upload success banner clickable.
 */
function setStatusBannerWithLink(
  elId: string,
  textElId: string,
  message: string,
  kind: StatusBannerKind,
  linkText: string,
  linkUrl: string,
): void {
  const el = document.getElementById(elId);
  const textEl = document.getElementById(textElId);
  if (!el || !textEl) return;

  const linkIndex = message.indexOf(linkText);
  if (linkIndex < 0) {
    setStatusBanner(elId, textElId, message, kind);
    return;
  }

  const before = message.slice(0, linkIndex);
  const after = message.slice(linkIndex + linkText.length);
  textEl.innerHTML = `${escapeHtml(before)}<a href="${escapeHtml(linkUrl)}" target="_blank" rel="noopener noreferrer" class="status-banner-link">${escapeHtml(linkText)}</a>${escapeHtml(after)}`;
  el.classList.remove(...STATUS_BANNER_KIND_CLASSES, "status-banner--visible");
  el.classList.add(`status-banner--${kind}`, "status-banner--visible");
}

function setMapUploadStatus(message: string, kind: StatusBannerKind = "pending"): void {
  setStatusBanner("map-upload-status", "map-upload-status-text", message, kind);
}

function setMapUploadStatusWithLink(message: string, kind: StatusBannerKind, linkText: string, linkUrl: string): void {
  setStatusBannerWithLink("map-upload-status", "map-upload-status-text", message, kind, linkText, linkUrl);
}

export function clearMapUploadStatus(): void {
  setMapUploadStatus("");
}

/**
 * Shows the department-missing banner next to the Select Order/Upload button, same visual
 * "mechanism" as the Upload status above -- see updateDepartmentStatusBanner, the only
 * caller. Dismissing it (the x button, wired in initMapUploadAction) never "sticks": the
 * next search attempt calls updateDepartmentStatusBanner again and re-shows it if the value
 * is still missing.
 */
function setOrderSearchStatus(message: string, kind: StatusBannerKind = "error"): void {
  setStatusBanner("order-search-status", "order-search-status-text", message, kind);
}

function clearOrderSearchStatus(): void {
  setOrderSearchStatus("");
}

// ─── Primary action mode ────────────────────

function getPrimaryActionMode(): PrimaryActionMode {
  if (selectedBcRecords.size === 0) return "none";
  const hasUploadableFiles = prepareUploadCandidates().length > 0;
  return hasUploadableFiles ? "upload" : "none";
}

export function updateMapUploadButtonState(): void {
  const btn = document.getElementById("btn-map-upload") as HTMLButtonElement | null;
  if (!btn) return;
  const mode = getPrimaryActionMode();
  let label = "No Action";
  if (mode === "upload") {
    label = selectedBcRecords.size > 1 ? `Upload to ${selectedBcRecords.size} Orders` : "Upload";
  } else if (selectedBcRecords.size === 0) {
    label = "Select Order";
  }
  btn.textContent = label;
  btn.disabled = mapUploadInProgress || mode === "none";
}

// ─── Primary action execution ───────────────

async function executePrimaryAction(): Promise<void> {
  const mode = getPrimaryActionMode();
  if (mode === "none") return;
  await uploadOnly();
}

// 1 initial attempt + up to 2 retries; delay doubles each retry (2s, then 4s). BC-side
// record locks/timeouts that cause this tend to clear within a few seconds, and this is
// per file-order pair, not the whole batch, so a genuinely failing upload doesn't stall
// everything behind it for long.
const UPLOAD_MAX_ATTEMPTS = 3;
const UPLOAD_RETRY_DELAY_MS = 2000;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

/**
 * Whether a failed upload attempt is worth retrying at all, and if so, whether it should
 * force a fresh token before the retry or just retry as-is:
 *  - 401 (Unauthorized): the token itself was rejected -- a plain retry with the same
 *    token would just fail identically, so this skips straight to a forced refresh.
 *  - 5xx, or no response at all (a network-level failure -- offline, DNS, TLS, a dropped
 *    connection -- fetch throws before/without a BcHttpError): possibly transient, worth
 *    a plain retry first before suspecting the token.
 *  - Anything else (400/403/404/other 4xx): a deterministic client-side failure --
 *    bad request, a genuine permission denial, not found, etc. Retrying (with or without
 *    a new token) will not change the outcome, so these are never retried. 403 in
 *    particular is deliberately excluded from the "refresh" path: 401 means "not
 *    authenticated" (the token itself is the problem), 403 means "authenticated but not
 *    permitted" -- a fresh token changes nothing about a genuine permission denial.
 */
type UploadRetryPlan = "none" | "plain-then-refresh" | "refresh-immediately";

function classifyUploadFailure(err: unknown): UploadRetryPlan {
  if (!(err instanceof BcHttpError)) return "plain-then-refresh"; // network-level failure
  if (err.status === 401) return "refresh-immediately";
  if (err.status >= 500) return "plain-then-refresh";
  return "none";
}

/**
 * Uploads one file to one order, retrying up to twice on a failure classified as
 * retryable by classifyUploadFailure -- see its doc comment for exactly which statuses
 * qualify and why 401 and 5xx are treated differently.
 *
 * For a 5xx/network failure, the first retry (attempt 2) changes nothing but time --
 * covers a genuinely transient BC-side condition (e.g. a SQL lock/deadlock) that a bare
 * retry alone should clear. Only if that ALSO fails does the final retry (attempt 3)
 * force a genuinely new access token via forceRefreshAccessToken (bypassing MSAL's own
 * cache, not just ours -- see its doc comment) before trying again. For a 401, that same
 * forced refresh happens immediately, before attempt 2, since a plain retry there has no
 * chance of succeeding. This is all still unconfirmed as the actual fix for what users
 * reported (an upload failing identically on every manual retry for several minutes, yet
 * succeeding immediately after restarting Outlook) -- see the logging below, which is
 * meant to gather evidence for that, not just to make the failure quieter.
 *
 * Also returns the (possibly refreshed) BcRequestContext so the caller can carry a working
 * token forward into later file/order pairs in the same batch, rather than only fixing the
 * one that happened to trigger the refresh.
 *
 * linkOrderDocument's POST is not idempotent -- it creates a new document record on every
 * call -- so before each retry this also confirms via isFileAlreadyLinked that the
 * "failed" attempt didn't actually succeed server-side (e.g. the insert committed but a
 * later step in the same BC call threw, wrapped into the same generic error). Skips the
 * retry and treats it as a success when it did, so a transient failure can never turn into
 * a duplicate attachment.
 */
async function uploadWithRetry(
  order: BcOperationalOrder,
  file: UploadCandidate,
  bytes: Uint8Array,
  uploadedBy: string,
  bc: BcRequestContext,
  onRetrying: (attempt: number, maxAttempts: number) => void,
): Promise<BcRequestContext> {
  let ctx = bc;
  const label = `"${file.fileName}" -> ${order.orderNo}`;
  for (let attempt = 1; attempt <= UPLOAD_MAX_ATTEMPTS; attempt++) {
    const attemptStartedAtMs = Date.now();
    try {
      const result = await linkOrderDocument(
        order.orderNo ?? "",
        file.fileName,
        bytes,
        file.doctype || "",
        file.comments || "",
        file.allowSynchToPortal ?? false,
        uploadedBy,
        ctx,
      );
      const durationMs = Date.now() - attemptStartedAtMs;
      console.info(
        `[upload] ${label}: attempt ${attempt}/${UPLOAD_MAX_ATTEMPTS} succeeded in ${durationMs}ms` +
        ` (client-request-id ${result.clientRequestId}${result.bcRequestId ? `, bc-request-id ${result.bcRequestId}` : ""})`,
      );
      return ctx;
    } catch (err: unknown) {
      const durationMs = Date.now() - attemptStartedAtMs;
      const status = err instanceof BcHttpError ? err.status : null;
      const code = err instanceof BcHttpError ? err.code : null;
      const clientRequestId = err instanceof BcHttpError ? err.clientRequestId : null;
      const bcRequestId = err instanceof BcHttpError ? err.bcRequestId : null;
      console.info(
        `[upload] ${label}: attempt ${attempt}/${UPLOAD_MAX_ATTEMPTS} failed after ${durationMs}ms` +
        ` (HTTP ${status ?? "network error"}${code ? `, code ${code}` : ""}` +
        `${clientRequestId ? `, client-request-id ${clientRequestId}` : ""}${bcRequestId ? `, bc-request-id ${bcRequestId}` : ""}):` +
        ` ${getErrorMessage(err)}`,
      );

      const plan = classifyUploadFailure(err);
      if (plan === "none" || attempt === UPLOAD_MAX_ATTEMPTS) throw err;

      let alreadyLinked = false;
      try {
        alreadyLinked = await isFileAlreadyLinked(file.fileName, order.orderNo ?? null, ctx);
      } catch {
        // Inconclusive -- fall through and retry as normal; worst case a genuine duplicate
        // is no worse than what a manual re-upload after this same error would risk today.
      }
      if (alreadyLinked) {
        console.info(`[upload] ${label}: already linked despite the error -- treating as success.`);
        return ctx;
      }

      // A 401 forces the refresh right away (a same-token retry can't succeed); a
      // 5xx/network failure only forces it ahead of the LAST attempt, after a plain retry
      // has already had its chance -- see the doc comment above.
      const shouldForceRefreshNow = plan === "refresh-immediately" || attempt === UPLOAD_MAX_ATTEMPTS - 1;
      if (shouldForceRefreshNow) {
        const beforeExpiry = getTokenExpiresAtMs();
        const freshToken = await forceRefreshAccessToken();
        const afterExpiry = getTokenExpiresAtMs();
        console.info(
          `[upload] ${label}: forcing a token refresh before the next attempt ` +
          (freshToken
            ? `(expiry ${new Date(beforeExpiry).toISOString()} -> ${new Date(afterExpiry).toISOString()}, changed: ${afterExpiry !== beforeExpiry})`
            : "(refresh failed, retrying with the existing token)"),
        );
        if (freshToken) ctx = { ...ctx, token: freshToken };
      }

      onRetrying(attempt + 1, UPLOAD_MAX_ATTEMPTS);
      await delay(UPLOAD_RETRY_DELAY_MS * attempt);
    }
  }
  return ctx; // unreachable -- every loop exit above returns or throws
}

/**
 * Uploads every candidate file to every selected order. Each file's bytes are read once
 * and reused across orders (rather than re-reading per order) to avoid redundant
 * Office.js attachment reads. Candidates are exactly the files rendered checked in the
 * attachments list (see isFileCheckedForUpload) -- an already-uploaded file defaults to
 * unchecked and is skipped, but re-uploading it is still just a checkbox click away, a
 * deliberate user choice this function never blocks.
 */
async function uploadOnly(): Promise<void> {
  if (mapUploadInProgress) return;
  try {
    if (selectedBcRecords.size === 0) throw new Error("Select at least one order first.");
    const candidates = prepareUploadCandidates();
    if (candidates.length === 0) throw new Error("No uploadable files selected.");

    const container = document.getElementById("attachments-list") as HTMLElement;
    if (!container || !validateUploadCandidates(container, candidates))
        throw new Error("Please select a Document Type for each selected file.");

    mapUploadInProgress = true;
    updateMapUploadButtonState();
    // let, not const -- uploadWithRetry can refresh the token mid-batch (see its doc
    // comment) and returns the updated context so later file/order pairs pick it up too.
    let bc = await getBcRequestContext(true);
    const uploadedBy = getCurrentAccount()?.username ?? "";

    const orders = Array.from(selectedBcRecords.values());
    let totalUploaded = 0;
    const errors: string[] = [];

    for (let ci = 0; ci < candidates.length; ci++) {
      const file = candidates[ci];
      let bytes: Uint8Array;
      try {
        bytes = await file.readBytes();
      } catch (err: unknown) {
        errors.push(`${file.fileName}: ${getErrorMessage(err) || "Read failed"}`);
        continue;
      }
      if (bytes.byteLength > MAX_UPLOAD_BYTES) {
        errors.push(`${file.fileName}: File too large (${formatSize(bytes.byteLength)}; limit is ${formatSize(MAX_UPLOAD_BYTES)}).`);
        continue;
      }

      for (let oi = 0; oi < orders.length; oi++) {
        const order = orders[oi];

        setMapUploadStatus(
          orders.length > 1
            ? `Uploading ${file.fileName} to order ${order.orderNo} (${oi + 1}/${orders.length})...`
            : `Uploading ${ci + 1}/${candidates.length}: ${file.fileName}`,
          "pending",
        );
        try {
          bc = await uploadWithRetry(order, file, bytes, uploadedBy, bc, (attempt, maxAttempts) => {
            setMapUploadStatus(
              `Retrying ${file.fileName} -> ${order.orderNo} (attempt ${attempt}/${maxAttempts})...`,
              "pending",
            );
          });
          totalUploaded++;
        } catch (err: unknown) {
          errors.push(`${file.fileName} -> ${order.orderNo}: ${getErrorMessage(err) || "Upload failed"}`);
        }
      }
    }

    if (onAttachmentsChanged) onAttachmentsChanged();
    if (errors.length === 0) {
      const linkText = `Uploaded ${totalUploaded} file(s) to ${orders.length} order(s)`;
      let ordersUrl: string | null = null;
      try {
        ordersUrl = await findOrdersUrlForOrderNos(orders.map((o) => o.orderNo ?? ""), bc);
      } catch (err: unknown) {
        // Best-effort: the upload itself already succeeded, so a failure to fetch the
        // link target just means a plain (non-clickable) confirmation is shown instead.
        console.error("Failed to fetch orders URL for upload status link:", getErrorMessage(err));
      }

      if (ordersUrl) {
        setMapUploadStatusWithLink(`Done. ${linkText}.`, "success", linkText, ordersUrl);
      } else {
        setMapUploadStatus(`Done. ${linkText}.`, "success");
      }
    } else {
      setMapUploadStatus(`Completed with errors. Uploaded ${totalUploaded}. ${errors.join(" | ")}`, "error");
    }
  } catch (err: unknown) {
    setMapUploadStatus(`Upload failed: ${getErrorMessage(err)}`, "error");
  } finally {
    mapUploadInProgress = false;
    updateMapUploadButtonState();
  }
}

// ─── Helpers ────────────────────────────────

// Mirrors renderFileRow's checked formula in attachments.ts exactly (isFileChecked from
// email-context.ts) so a row shown unchecked (because it's already linked in BC) is never
// silently included as an upload candidate, while a row the user manually re-checked (to
// force a re-upload) is always included -- what's visibly checked is what gets uploaded.
function isFileCheckedForUpload(id: string, fileName: string): boolean {
  const isUploaded = isFileNameUploaded(normalizeFileNameForCompare(fileName));
  return isFileChecked(id, isUploaded);
}

function prepareUploadCandidates(): UploadCandidate[] {
  const candidates: UploadCandidate[] = [];
  // Dedup within this one batch only (e.g. an email's .eml and an identically-named
  // attachment) -- a file already uploaded to BC in a past session is not excluded here
  // via a blocklist; it's excluded (by default) only because it renders unchecked, same as
  // any other unchecked file. The "uploaded" badge is informational, never blocking: the
  // user can still check the box and re-upload deliberately.
  const queuedNames = new Set<string>();
  const canQueue = (name: string): boolean => {
    const key = normalizeFileNameForCompare(name);
    if (!key) return false;
    if (queuedNames.has(key)) return false;
    queuedNames.add(key);
    return true;
  };

  collectedEmails.forEach((email) => {
    const emailItemId = getEmailFileId(email.key);
    const emlName = normalizeEmlFileName(email.subject || "email");
    if (isFileCheckedForUpload(emailItemId, emlName)) {
      const details = getOrCreateAttachmentDetails(emailItemId);
      if (canQueue(emlName)) {
        candidates.push({
          id: emailItemId,
          fileName: emlName,
          doctype: details.docType || "",
          comments: details.comment || "",
          allowSynchToPortal: details.allowSynchToPortal ?? false,
          readBytes: () => getEmailItemEmlBytes(email.key),
        });
      }
    }

    email.attachments.forEach((a) => {
      const fileId = getAttachmentFileId(email.key, a.id);
      const fileName = a.name ?? "attachment";
      if (!isFileCheckedForUpload(fileId, fileName)) return;
      const details = getOrCreateAttachmentDetails(fileId);
      if (!canQueue(fileName)) return;
      candidates.push({
        id: fileId,
        fileName,
        doctype: details.docType || "",
        comments: details.comment || "",
        allowSynchToPortal: details.allowSynchToPortal ?? false,
        readBytes: () => getEmailAttachmentBytes(email.key, a.id),
      });
    });
  });

  localFiles.forEach((local) => {
    if (!isFileCheckedForUpload(local.id, local.file.name)) return;
    if (!canQueue(local.file.name)) return;
    const details = getOrCreateAttachmentDetails(local.id);
    candidates.push({
      id: local.id,
      fileName: local.file.name,
      doctype: details.docType || "",
      comments: details.comment || "",
      allowSynchToPortal: details.allowSynchToPortal ?? false,
      readBytes: async () => new Uint8Array(await local.file.arrayBuffer()),
    });
  });

  return candidates;
}

export function validateUploadCandidates(container: HTMLElement, candidates: UploadCandidate[]): boolean {
    let valid = true;

    container.querySelectorAll<HTMLElement>(".file-category-button")
        .forEach(btn => btn.classList.remove("file-category-button--error"));

    for (const candidate of candidates) {
        const details = getOrCreateAttachmentDetails(candidate.id);
        if (details?.docType) continue;

        const wrapper = Array.from(container.querySelectorAll<HTMLElement>(".file-category"))
                              .find(w => w.dataset.id === candidate.id);

        const button = wrapper?.querySelector<HTMLElement>(".file-category-button");
        button?.classList.add("file-category-button--error");
        valid = false;
    }
    return valid;
}

// ─── Attachment content reading ─────────────

/**
 * attachmentId here was captured during an earlier, separate loadItemByIdAsync call (when
 * the email was first collected/synced -- see syncCollectionFromSelection in
 * email-context.ts), not the fresh one this function just made. Office.js doesn't
 * guarantee an attachment id stays identical across two separate loads of the same item in
 * every host, and classic Outlook's local attachment cache in particular has been observed
 * to disagree with itself here -- suspected cause of an upload failing/erroring for one
 * specific attachment while the same email's .eml (which never depends on an attachment id
 * -- see getEmailItemEmlBytes) keeps working fine.
 *
 * Rather than trust the old id blindly, this checks it against the freshly-loaded item's
 * own attachment list first; if it's gone, it rematches by name+size (captured at
 * collection time, from before staleness could have set in) and uses whatever id the fresh
 * load actually has for that file. Self-healing -- no Outlook restart required -- and only
 * changes behavior when the id genuinely doesn't resolve; the common case (id still valid)
 * is a single array scan with no extra API calls.
 */
function resolveFreshAttachmentId(item: unknown, email: CollectedEmail, attachmentId: string): string {
  const freshAttachments = (item as LoadedMessageExt).attachments ?? [];
  if (freshAttachments.some((a) => a.id === attachmentId)) return attachmentId;

  const known = email.attachments.find((a) => a.id === attachmentId);
  const knownSize = known ? (known as AttachmentDetailsExt).size : undefined;
  const rematch = known
    ? freshAttachments.find(
        (a) => a.name === known.name && (knownSize == null || (a as AttachmentDetailsExt).size === knownSize),
      )
    : undefined;
  if (rematch) {
    console.warn(
      `Attachment id ${attachmentId} ("${known?.name}") no longer resolves on a fresh item reload; ` +
      `using rematched id ${rematch.id} instead.`,
    );
    return rematch.id;
  }

  // No safe rematch found -- fall through with the original id so the existing
  // "attachment not found" error from Office.js still applies, rather than guessing.
  return attachmentId;
}

// Both content-read functions below go through withMailItem (email-context.ts), which uses
// Outlook's already-open item when it's the right one and otherwise loads it via
// loadItemByIdAsync strictly one at a time -- see the section comment there for why. Each
// attempt also re-acquires the item from scratch. Users have reported .eml/attachment
// content failing with Office.js's own generic "An internal error has occurred." in classic
// Outlook -- no HTTP status, no BC involvement at all, since this happens before the file
// is ever sent. Unlike the upload retry in uploadWithRetry, reading is idempotent -- no
// duplicate-document risk -- so this just retries plainly.
const READ_MAX_ATTEMPTS = 3;
const READ_RETRY_DELAY_MS = 500;

async function getEmailAttachmentBytes(emailKey: string, attachmentId: string): Promise<Uint8Array> {
  const email = collectedEmails.find((e) => e.key === emailKey);
  if (!email?.itemId) throw new Error("Attachment source email item is unavailable.");
  const target = { itemId: email.itemId, internetMessageId: email.key };
  const label = `"${email.subject}" attachment ${attachmentId}`;

  return withRetry(
    () =>
      withMailItem(target, async (item) => {
        const resolvedAttachmentId = resolveFreshAttachmentId(item, email, attachmentId);
        const content = await getAttachmentContentAsync(item as OfficeMessageItem, resolvedAttachmentId);
        const format = String(content?.format ?? "").toLowerCase();
        const payload = String(content?.content ?? "");

        if (format === "base64" || format === "eml") return base64ToUint8Array(payload);
        if (format === "url" || format === "ewsid") throw new Error("Cloud attachment type is not supported for upload.");
        if (!payload) throw new Error("Empty attachment payload.");
        return base64ToUint8Array(payload);
      }),
    READ_MAX_ATTEMPTS,
    READ_RETRY_DELAY_MS,
    (attempt, err) => {
      console.info(`[read] ${label}: attempt ${attempt}/${READ_MAX_ATTEMPTS} failed (${getErrorMessage(err)}) -- retrying.`);
    },
  );
}

async function getEmailItemEmlBytes(emailKey: string): Promise<Uint8Array> {
  if (!Office.context.requirements.isSetSupported("Mailbox", "1.14")) {
    throw new Error("EML export requires Mailbox 1.14 support.");
  }

  const email = collectedEmails.find((e) => e.key === emailKey);
  if (!email?.itemId) throw new Error("Email source item is unavailable.");
  const target = { itemId: email.itemId, internetMessageId: email.key };
  const label = `"${email.subject}" .eml`;

  return withRetry(
    () =>
      withMailItem(target, async (item) => {
        const base64Eml = await getAsFileAsync(item as OfficeMessageItem);
        if (!base64Eml) throw new Error("Empty EML payload.");
        return base64ToUint8Array(base64Eml);
      }),
    READ_MAX_ATTEMPTS,
    READ_RETRY_DELAY_MS,
    (attempt, err) => {
      console.info(`[read] ${label}: attempt ${attempt}/${READ_MAX_ATTEMPTS} failed (${getErrorMessage(err)}) -- retrying.`);
    },
  );
}

interface AttachmentContentResult {
  content: string;
  format: string;
}

interface OfficeMessageItem {
  getAttachmentContentAsync(
    attachmentId: string,
    callback: (result: Office.AsyncResult<AttachmentContentResult>) => void,
  ): void;
  getAsFileAsync?(callback: (result: Office.AsyncResult<string>) => void): void;
}

function getAttachmentContentAsync(item: OfficeMessageItem, attachmentId: string): Promise<AttachmentContentResult> {
  return new Promise((resolve, reject) => {
    item.getAttachmentContentAsync(attachmentId, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message ?? "getAttachmentContentAsync failed"));
      }
    });
  });
}

function getAsFileAsync(item: OfficeMessageItem): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!item?.getAsFileAsync) {
      reject(new Error("EML export is not available for this message."));
      return;
    }
    item.getAsFileAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(String(result.value ?? ""));
      } else {
        reject(new Error(result.error?.message ?? "getAsFileAsync failed"));
      }
    });
  });
}

// ─── Reset ──────────────────────────────────

export function resetOrderState(): void {
  allOrders = [];
  selectedBcRecords = new Map();
  autoSelectedOrderKeys = new Set();
  // Callers that reset order state directly (sign-out/Clear Settings, company change) don't
  // necessarily follow up with a loadOrders() call that would otherwise re-evaluate this --
  // clear it explicitly so a department-missing banner from before never lingers stale.
  clearOrderSearchStatus();
}
